import React from "react";
import { Request } from "../../hooks/Request";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import CuadroComparativoTransportePDF from "../PDFs/CuadroComparativoTransportePDF";
import { Button } from "../Buttons";
import Circle from "../Animation/Circle";
import * as dayjs from "dayjs";

const listFields = [
  "subastaDeTransporte",
  "tipoDeImportacion",
  "justificaciónYObservacionesAdicionales",
  "fechaDeRecojo",
  "volumen",
  "pesoBruto",
  "direcciónDeRecojo",
  "modalidadDeDespacho",
  "tipoDeDespacho",
  "descripciónGeneralDeLaCompra",
  "descripcionGeneralDeLaCompra",
  "nombreDelProductoReferencial",
  "producto",
  "codigoDeInterno",
  "proveedor",
  "modalidadCargaTransporte",
  "modalidadDeCarga",
  "departamentoSolicitante",
  "revisor",
  "primerAprobador",
  "seleccionarAprobador2",
  "observacionesDeLaPrimeraAprobación",
  "comentariosDeLaSegundaAprobación",
  "incotermServicio",
  "cotizaciónDeProveedores",
  "régimen/PatrónImportación",
  "almacénDestino",
  "cantidad",
  "tiempoEntrega(Días)",
  "condiciones",
];

const toDisplayValue = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;

  if (typeof value === "object") {
    return (
      value.name ||
      value.label ||
      value.razonSocial ||
      value.value ||
      value.description ||
      fallback
    );
  }

  return String(value);
};

const getFirstValue = (...values) => {
  const found = values.find(
    (value) => value !== null && value !== undefined && value !== "",
  );

  return found ?? "";
};

export const ComparativeTransportPdf = (props) => {
  const { procedureId } = props;

  const queryParams = listFields.map((field) => `fieldnames=${field}`).join("&");

  const { data, loading, error } = Request({
    urlApi: `/Procedure/${procedureId}/dataValues?${queryParams}`,
  });

  const { data: routeData, loading: loadingRoute } = Request({
    urlApi: `/Tradecruz/${procedureId}/HojaDeRuta`,
  });

  if (error) {
    return (
      <div className="p-4 text-center text-red-500">
        Error: {error.message}
      </div>
    );
  }

  if (!loading || !loadingRoute || !data) return <Circle />;

  const formatedDate = (date) =>
    date ? new Date(date).toLocaleDateString() : "-";

  const proveedoresRaw = data.subastaDeTransporteSubDatas || [];

  const cotizacionSubDatas = data.cotizaciónDeProveedoresSubDatas || [];
  const fallbackIncoterm = data.condiciones
    || cotizacionSubDatas
    .map((c) => {
      try { return JSON.parse(c.description2).incoterms; } catch { return null; }
    })
    .find(Boolean) || data.incotermServicio || "";

  const providersParsed = proveedoresRaw.map((p, i) => {
    let parsed = {};

    if (typeof p.description2 === "string") {
      try {
        parsed = JSON.parse(p.description2);
      } catch (e) {}
    } else {
      parsed = p.description2 || {};
    }

    const price = parsed.precio !== undefined ? parsed.precio : p.numberValue || 0;
    const generalQty = data["cantidad"] ? parseFloat(data["cantidad"]) : 0;
    const qty = generalQty || (parsed.cantidad !== undefined ? parsed.cantidad : 1);

    return {
      id: p.id,
      titulo: `PROVEEDOR ${i + 1}`,
      nombre: parsed.providerName || p.label || "Sin nombre",
      total: Number(price) * Number(qty),
      cantidad: qty,
      cUnit: price,
      isRecommended: p.description3 === "true",
      paymentTerms: parsed.formaPago || "",
      incoterms: parsed.incoterms || parsed.incoterm || fallbackIncoterm,
      deliveryDate: toDisplayValue(data["tiempoEntrega(Días)"]) || parsed.tiempoEntrega || "",
      almacenDestino: toDisplayValue(data["almacénDestino"]) || parsed.almacenDestino || "",
      logisticCosts: [],
    };
  });

  const recommendedProvider = providersParsed.find((s) => s.isRecommended);

  const lugarEntregaGeneral =
    recommendedProvider?.almacenDestino ||
    providersParsed.find((provider) => provider.almacenDestino)?.almacenDestino ||
    "-";

  const fechaElaboracion = data.procFechaDeInicio
    ? dayjs(data.procFechaDeInicio).format("DD/MM/YYYY")
    : dayjs().format("DD/MM/YYYY");

  const fechaBrutaRecojo = data.fechaDeRecojo;

  const fechaRecojoFormateada = fechaBrutaRecojo
    ? dayjs(fechaBrutaRecojo).format("DD/MM/YYYY")
    : "-";

  const producto = toDisplayValue(
    getFirstValue(
      data.descripciónGeneralDeLaCompra,
      data.descripcionGeneralDeLaCompra,
      data.nombreDelProductoReferencial,
      data.producto,
    ),
  );

  const modalidadCarga = toDisplayValue(
    getFirstValue(data.modalidadCargaTransporte, data.modalidadDeCarga),
  );

  const modalidadDespacho = toDisplayValue(
    getFirstValue(data.modalidadDeDespacho, data.tipoDeDespacho, data["régimen/PatrónImportación"]),
  );

  const observaciones = toDisplayValue(
    getFirstValue(
      data.justificaciónYObservacionesAdicionales,
      data.observacionesDeLaPrimeraAprobación,
      data.comentariosDeLaSegundaAprobación,
    ),
    "",
  );

  const items = [
    {
      item: "1",
      cantidad: "1",
      unidad: "SRV",
      detalle:
        "Servicio de Logística de Transporte Multimodal / Aereo / Terrestre Internacional.",
      ofertas: [0, 1, 2].map((i) => {
        const sup = providersParsed[i];

        if (!sup) return { cUnit: "", cTotal: "", obs: "" };

        return {
          provId: sup.id,
          cantidad: sup.cantidad,
          cUnit: sup.cUnit?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
          }),
          cTotal: sup.total?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
          }),
          obs: sup.almacenDestino ? `Destino: ${sup.almacenDestino}` : "",
        };
      }),
    },
  ];

    const defaultAuthorizations = {
    elaborado: {
      nombre: "Mercedes Arciénega",
      cargo: "Analista De Importaciones",
      fecha: "19/01/2026",
    },
    revisado: {
      nombre: "Gabriela Miranda",
      cargo: "Responsable De Importaciones",
      fecha: "14/01/2026",
    },
    aprobado: {
      nombre: "Zulema Andia",
      cargo: "Jefe Administrativo",
      fecha: "15/01/2026",
    },
  };

  const approvalHistory = routeData?.procedureHistories?.find(
    (history) => history.step === 5,
  );

  const approvalDate = formatedDate(approvalHistory?.finalDate);

  const info = {
    departamento: data.departamentoSolicitante || "",
    descripcion: producto || "Servicio logístico de importación.",
    nroPedido: data.codigoDeInterno || process.env.REACT_APP_PREFIX || "-",
    codigoDoc: "PR-ADQ-123-R166",
    versionDoc: "5",
    tipoCompraActual: data.tipoDeImportacion || "IMPORTACIÓN",

    general: {
      numeroImportacion: data.codigoDeInterno || "-",
      proveedor: toDisplayValue(data.proveedor),
      producto: producto || "-",
      incoterms: providersParsed.find((p) => p.incoterms)?.incoterms || fallbackIncoterm,
      fechaElaboracion,
    },

    operativa: {
      lugarRecojo: toDisplayValue(data.direcciónDeRecojo),
      lugarEntrega: lugarEntregaGeneral,
      fechaRecojo: fechaRecojoFormateada,
      modalidadDespacho,
      modalidadCarga,
      pesoBrutoKg: toDisplayValue(data.pesoBruto),
      cantidadTipoContenedores: toDisplayValue(data.cantidad),
    },

    fechaInicio: fechaElaboracion,
    direccionRecojo: toDisplayValue(data.direcciónDeRecojo),
    lugarEntrega: lugarEntregaGeneral,
    tipoDespacho: modalidadDespacho,
    pesoBruto: toDisplayValue(data.pesoBruto),
    volumen: toDisplayValue(data.volumen),
    fechaRecojo: fechaRecojoFormateada,
    incotermServicio: providersParsed.find((p) => p.incoterms)?.incoterms || "-",

    proveedores: [0, 1, 2].map(
      (i) => providersParsed[i] || { nombre: "", total: 0, logisticCosts: [] },
    ),
    uniqueLogisticCostTypes: [],
    items,
    recomendado: recommendedProvider,
    justificacion: data.justificaciónYObservacionesAdicionales || "",
    observaciones,

        firmas: [
      {
        firma: "ELABORADO POR:",
        nombre: defaultAuthorizations.elaborado.nombre,
        cargo: defaultAuthorizations.elaborado.cargo,
        fecha: defaultAuthorizations.elaborado.fecha,
      },
      {
        firma: "REVISADO POR:",
        nombre: toDisplayValue(data.revisor, defaultAuthorizations.revisado.nombre),
        cargo: defaultAuthorizations.revisado.cargo,
        fecha: defaultAuthorizations.revisado.fecha,
      },
      {
        firma: "APROBADO POR:",
        nombre:
          approvalHistory?.userInChange ||
          toDisplayValue(data.primerAprobador, "") ||
          toDisplayValue(data.seleccionarAprobador2, "") ||
          defaultAuthorizations.aprobado.nombre,
        cargo: defaultAuthorizations.aprobado.cargo,
        fecha:
          approvalDate && approvalDate !== "-"
            ? approvalDate
            : defaultAuthorizations.aprobado.fecha,
        observaciones: data.comentariosDeLaSegundaAprobación || "",
      },
    ],
  };

  const pdfDoc = <CuadroComparativoTransportePDF info={info} />;

  return (
    <div className="flex flex-col w-[80vw] bg-white rounded-lg shadow-md overflow-hidden">
      <div className="flex justify-between items-center px-4 py-2 bg-gray-100 border-b border-gray-200">
        <h2 className="text-base font-bold text-gray-800">
          Cuadro Comparativo de Transporte — {data.codigoDeInterno || procedureId}
        </h2>

        <PDFDownloadLink
          document={pdfDoc}
          fileName={`Cuadro_Comparativo_Transporte_${
            data.codigoDeInterno || procedureId
          }.pdf`}
        >
          {({ loading: pdfLoading }) => (
            <Button className="py-1 px-4 text-sm" disabled={pdfLoading}>
              <i className="fas fa-file-download mr-2"></i>
              {pdfLoading ? "Preparando..." : "Descargar PDF"}
            </Button>
          )}
        </PDFDownloadLink>
      </div>

      <PDFViewer width="100%" height="800px" style={{ border: "none" }}>
        {pdfDoc}
      </PDFViewer>
    </div>
  );
};